import { Fragment, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { apiClient } from '@services/api'
import { ProductPicker } from '../components/ProductPicker'

type Unit = 'kg' | 'gms' | 'pkt' | 'Tin' | 'ltr'
interface Supplier { id: number; name: string }
interface Product { id: number; name: string; unit: Unit }
interface PoLineInput { id: string; product: string; unit?: Unit; qty: number; rate: number }
const UNITS: Unit[] = ['kg', 'gms', 'pkt', 'Tin', 'ltr']
interface PoLine { product: string; quantity: number; unit: string; rate: number; amount: number; received: number; remaining: number }
interface Po {
  id: number
  poNumber: string
  supplierName: string
  orderDate: string
  expectedDate: string | null
  status: 'Draft' | 'Sent' | 'Partially Received' | 'Fully Received' | 'Cancelled' | 'Closed'
  lines: PoLine[]
  orderedLabel: string
  receivedLabel: string
  receivedPct: number
  value: number
  overdueDays: number
  bills: Array<{ id: number; billNo: string; billDate: string; amount: number }>
}

const today = () => new Date().toISOString().split('T')[0]
const plusDays = (d: number) => { const t = new Date(); t.setDate(t.getDate() + d); return t.toISOString().split('T')[0] }
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const blank = (id: string): PoLineInput => ({ id, product: '', qty: NaN, rate: NaN })
const EMPTY_SUPPLIER = { name: '', phone: '', address: '', gstin: '' }
const EMPTY_PRODUCT = { name: '', nameTamil: '', category: '', hsnCode: '', gstRate: 0, unit: 'kg' as Unit }
const statusBadge: Record<Po['status'], string> = { Draft: 'badge-draft', Sent: 'badge-sent', 'Partially Received': 'badge-partial', 'Fully Received': 'badge-full', Cancelled: 'badge-draft', Closed: 'badge-ok' }

export function PurchaseOrders() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [supplierId, setSupplierId] = useState('')
  const [orderDate, setOrderDate] = useState(today())
  const [expectedDate, setExpectedDate] = useState(plusDays(5))
  const [lines, setLines] = useState<PoLineInput[]>([blank('1')])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)

  const { data: suppliersData, refetch: refetchSuppliers } = useQuery({ queryKey: ['suppliers-list'], queryFn: () => apiClient.get<any>('/suppliers') })
  const { data: productsData, refetch: refetchProducts } = useQuery({ queryKey: ['products-list'], queryFn: () => apiClient.get<any>('/products') })

  // Inline "add new supplier" / "add new product" panels, same as Purchase Entry.
  const [showNewSupplier, setShowNewSupplier] = useState(false)
  const [newSupplier, setNewSupplier] = useState(EMPTY_SUPPLIER)
  const [newSupplierError, setNewSupplierError] = useState('')
  const [savingSupplier, setSavingSupplier] = useState(false)
  const [showNewProduct, setShowNewProduct] = useState(false)
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT)
  const [newProductError, setNewProductError] = useState('')
  const [savingProduct, setSavingProduct] = useState(false)

  const saveSupplier = async () => {
    setNewSupplierError('')
    if (!newSupplier.name.trim()) return setNewSupplierError('Supplier name is required')
    if (newSupplier.phone && !/^\d{10}$/.test(newSupplier.phone)) return setNewSupplierError('Phone must be a 10-digit mobile number')
    setSavingSupplier(true)
    try {
      const res = await apiClient.post<any>('/suppliers', newSupplier)
      await refetchSuppliers()
      setSupplierId(String(res.data.id))
      clearError('supplier')
      setNewSupplier(EMPTY_SUPPLIER)
      setShowNewSupplier(false)
      setNotice({ kind: 'success', text: `Supplier "${res.data.name}" saved and selected for this order.` })
    } catch (err: any) {
      setNewSupplierError(err.response?.data?.message || 'Failed to save supplier')
    } finally {
      setSavingSupplier(false)
    }
  }

  const saveProduct = async () => {
    setNewProductError('')
    if (!newProduct.name.trim()) return setNewProductError('Product name is required')
    if (!/^\d{4,8}$/.test(newProduct.hsnCode.replace(/\s+/g, ''))) return setNewProductError('HSN code is required — 4 to 8 digits, as printed on the supplier bill (e.g. 0713 or 09109100)')
    setSavingProduct(true)
    try {
      const res = await apiClient.post<any>('/products', newProduct)
      await refetchProducts()
      const created = res.data
      const line: PoLineInput = { id: String(Math.max(0, ...lines.map((l) => parseInt(l.id))) + 1), product: created?.name || newProduct.name.trim(), unit: (created?.unit || newProduct.unit) as Unit, qty: NaN, rate: NaN }
      const emptyOnly = lines.length === 1 && !lines[0].product.trim()
      setLines(emptyOnly ? [{ ...line, id: '1' }] : [...lines, line])
      setNewProduct(EMPTY_PRODUCT)
      setShowNewProduct(false)
      setNotice({ kind: 'success', text: `Product "${line.product}" added to the catalogue and to this order.` })
    } catch (err: any) {
      setNewProductError(err.response?.data?.message || 'Failed to save product')
    } finally {
      setSavingProduct(false)
    }
  }
  const { data: poData, refetch } = useQuery({ queryKey: ['purchase-orders', showAll], queryFn: () => apiClient.get<any>(`/purchase-orders${showAll ? '' : '?open=true'}`) })
  const suppliers: Supplier[] = suppliersData?.data?.items || []
  const products: Product[] = productsData?.data?.items || []
  const pickerOptions = useMemo(() => products.map((p: any) => ({ value: p.name, label: p.name, sub: p.nameTamil || null, right: `per ${p.unit}`, rightSub: p.category || null })), [products])
  const pos: Po[] = poData?.data?.items || []
  const summary = poData?.data?.summary
  const hint: string | null = poData?.data?.hint || null

  const catalogueUnit = (name: string): Unit | undefined => products.find((p) => p.name.toLowerCase() === name.trim().toLowerCase())?.unit
  const unitFor = (l: PoLineInput): Unit => l.unit || catalogueUnit(l.product) || 'kg'
  const clearError = (k: string) => setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n })
  const updateLine = (id: string, patch: Partial<PoLineInput>) => {
    setLines((ls) => ls.map((l) => {
      if (l.id !== id) return l
      // Changing the product re-defaults the unit from the catalogue; the user can still override it.
      const next = { ...l, ...patch }
      if (patch.product !== undefined) next.unit = catalogueUnit(patch.product) || l.unit
      return next
    }))
    Object.keys(patch).forEach((k) => clearError(`line-${id}-${k}`))
  }
  const addLine = () => setLines((ls) => [...ls, blank(String(Math.max(0, ...ls.map((l) => parseInt(l.id))) + 1))])
  const removeLine = (id: string) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.id !== id) : ls))
  const resetForm = () => { setSupplierId(''); setOrderDate(today()); setExpectedDate(plusDays(5)); setLines([blank('1')]); setEditingId(null); setErrors({}) }
  const total = lines.reduce((s, l) => s + (l.qty || 0) * (l.rate || 0), 0)

  const validate = () => {
    const e: Record<string, string> = {}
    if (!supplierId) e.supplier = 'Select a supplier'
    if (!orderDate) e.orderDate = 'Order date is required'
    if (expectedDate && orderDate && expectedDate < orderDate) e.expectedDate = 'Cannot be before the order date'
    const real = lines.filter((l) => l.product.trim() || l.qty > 0)
    if (real.length === 0) e.lines = 'Add at least one item'
    real.forEach((l) => {
      if (!l.product.trim()) e[`line-${l.id}-product`] = 'Product required'
      if (!(l.qty > 0)) e[`line-${l.id}-qty`] = 'Qty above 0'
      if (!(l.rate >= 0)) e[`line-${l.id}-rate`] = 'Rate required'
    })
    const names = real.map((l) => l.product.trim().toLowerCase())
    names.forEach((n, i) => { if (n && names.indexOf(n) !== i) e[`line-${real[i].id}-product`] = 'Listed twice' })
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = async (status: 'Sent' | 'Draft') => {
    setNotice(null)
    if (!validate()) return
    setBusy(status)
    try {
      const payload = { supplierId: Number(supplierId), orderDate, expectedDate: expectedDate || null, status, lines: lines.filter((l) => l.product.trim()).map((l) => ({ product: l.product.trim(), quantity: l.qty, rate: l.rate, unit: unitFor(l) })) }
      const res = editingId
        ? await apiClient.patch<any>(`/purchase-orders/${editingId}`, payload).then(async (r) => (status === 'Sent' ? apiClient.post<any>(`/purchase-orders/${editingId}/send`, {}) : r))
        : await apiClient.post<any>('/purchase-orders', payload)
      setNotice({ kind: 'success', text: `${res.message} · open ${res.data?.poNumber || 'it'} to print or download.` })
      if (res.data?.id) setOpenId(res.data.id)
      resetForm()
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] })
      queryClient.invalidateQueries({ queryKey: ['home-dashboard'] })
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.response?.data?.message || 'Could not save the purchase order' })
    } finally {
      setBusy(null)
    }
  }

  const action = async (po: Po, what: 'send' | 'cancel' | 'close' | 'delete') => {
    setNotice(null)
    setBusy(`${what}-${po.id}`)
    try {
      const res = what === 'delete' ? await apiClient.delete<any>(`/purchase-orders/${po.id}`) : await apiClient.post<any>(`/purchase-orders/${po.id}/${what}`, {})
      setNotice({ kind: 'success', text: res.message })
      if (what === 'delete' && editingId === po.id) resetForm()
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] })
      queryClient.invalidateQueries({ queryKey: ['home-dashboard'] })
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.response?.data?.message || 'Action failed' })
    } finally {
      setBusy(null)
    }
  }

  const editDraft = (po: Po) => {
    const sup = suppliers.find((s) => s.name.split(' — ')[0] === po.supplierName || s.name === po.supplierName)
    setSupplierId(sup ? String(sup.id) : '')
    setOrderDate(po.orderDate)
    setExpectedDate(po.expectedDate || '')
    setLines(po.lines.map((l, i) => ({ id: String(i + 1), product: l.product, unit: (l.unit as Unit) || undefined, qty: l.quantity, rate: l.rate })))
    setEditingId(po.id)
    setErrors({})
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div>
      <div className="hdr">
        <h1>Purchase Orders</h1>
        <p>Optional — place an order to a supplier before goods arrive. Ad-hoc purchase bills work fine without one too.</p>
      </div>

      {notice && <div className={`alert ${notice.kind === 'success' ? 'alert-success' : 'alert-error'}`}>{notice.text}</div>}

      <div>
        <div className="card">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>{editingId ? `Edit Draft ${pos.find((p) => p.id === editingId)?.poNumber || ''}` : 'New Purchase Order'}</h2>
            {editingId && <button type="button" className="btn-ghost btn-xs" onClick={resetForm}>Cancel edit</button>}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div>
              <div className="field-label-row">
                <label className="!m-0">Supplier</label>
                <button type="button" className={`btn-ghost btn-xs ${showNewSupplier ? 'on' : ''}`} onClick={() => setShowNewSupplier((v) => !v)}>+ Add new supplier</button>
              </div>
              <select value={supplierId} onChange={(e) => { setSupplierId(e.target.value); clearError('supplier') }} className={`mt-[5px] ${errors.supplier ? 'error' : ''}`}>
                <option value="">— Select supplier —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {errors.supplier && <div className="hint-error">{errors.supplier}</div>}
            </div>
            <div>
              <label>Order Date</label>
              <input type="date" value={orderDate} max={today()} onChange={(e) => { setOrderDate(e.target.value); clearError('orderDate') }} />
              {errors.orderDate && <div className="hint-error">{errors.orderDate}</div>}
            </div>
            <div>
              <label>Expected Delivery</label>
              <input type="date" value={expectedDate} min={orderDate} onChange={(e) => { setExpectedDate(e.target.value); clearError('expectedDate') }} />
              {errors.expectedDate && <div className="hint-error">{errors.expectedDate}</div>}
              <div className="hint">Overdue POs are flagged here and on the home dashboard.</div>
            </div>
          </div>
          {showNewSupplier && (
            <div className="bg-primary-soft rounded-[6px] p-[14px] mt-3">
              <div className="row2">
                <div><label>Supplier Name</label><input type="text" placeholder="e.g. Ganesh Spice Traders" value={newSupplier.name} onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} /></div>
                <div><label>Phone</label><input type="text" placeholder="10-digit mobile" value={newSupplier.phone} onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })} /></div>
              </div>
              <div className="mt-3">
                <label>Address <small>— door no., street, town, PIN</small></label>
                <textarea rows={2} placeholder="e.g. 14/2 Bazaar Street, Bodinayakanur — 625513" value={newSupplier.address} onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })} />
              </div>
              <div className="row2 mt-3">
                <div><label>GSTIN <small>— optional</small></label><input type="text" value={newSupplier.gstin} onChange={(e) => setNewSupplier({ ...newSupplier, gstin: e.target.value })} /></div>
              </div>
              {newSupplierError && <div className="hint-error">{newSupplierError}</div>}
              <div className="flex gap-2 mt-3">
                <button type="button" className="btn-accent btn-sm" disabled={savingSupplier} onClick={saveSupplier}>{savingSupplier ? 'Saving…' : 'Save & Use for This Order'}</button>
                <button type="button" className="btn-ghost btn-sm" onClick={() => { setShowNewSupplier(false); setNewSupplier(EMPTY_SUPPLIER); setNewSupplierError('') }}>Cancel</button>
              </div>
              <div className="hint">Saved as a permanent supplier record — appears in the list for every future order and bill.</div>
            </div>
          )}

          <div className="field-label-row mt-[18px]">
            <label className="!m-0">Line Items</label>
            <button type="button" className={`btn-ghost btn-xs ${showNewProduct ? 'on' : ''}`} onClick={() => setShowNewProduct((v) => !v)}>+ Add new product</button>
          </div>
          {showNewProduct && (
            <div className="bg-primary-soft rounded-[6px] p-[14px] mt-2 mb-2">
              <div className="row2">
                <div><label>Product Name</label><input type="text" placeholder="e.g. Cumin Seeds (Jeera)" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} /></div>
                <div>
                  <label>Unit</label>
                  <select value={newProduct.unit} onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value as Unit })}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
                  <div className="hint">{`Bought per ${newProduct.unit === 'pkt' ? 'piece' : newProduct.unit} and sold as purchased${newProduct.unit === 'kg' ? ' — tick Repack in Settings › Products if it is repacked into packs' : ''}.`}</div>
                </div>
              </div>
              <div className="row3">
                <div><label>HSN Code <small style={{ color: 'var(--danger, #c0392b)' }}>— required</small></label><input type="text" inputMode="numeric" placeholder="e.g. 09109100" maxLength={8} value={newProduct.hsnCode} data-testid="new-product-hsn" onChange={(e) => setNewProduct({ ...newProduct, hsnCode: e.target.value.replace(/\D/g, '') })} /></div>
              </div>
              <div className="row3">
                <div><label>Tamil Name <small>— printed under the item on invoices</small></label><input type="text" placeholder="e.g. ஜீரகம்" value={newProduct.nameTamil} onChange={(e) => setNewProduct({ ...newProduct, nameTamil: e.target.value })} /></div>
                <div>
                  <label>Category <small>— for category-wise reports</small></label>
                  <input type="text" list="po-product-categories" placeholder="e.g. Spices" value={newProduct.category} onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })} />
                  <datalist id="po-product-categories">{[...new Set(products.map((p: any) => p.category).filter(Boolean))].sort().map((c) => <option key={String(c)} value={String(c)} />)}</datalist>
                </div>
                <div>
                  <label>Selling GST %</label>
                  <select value={newProduct.gstRate} onChange={(e) => setNewProduct({ ...newProduct, gstRate: Number(e.target.value) })}>{[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}</select>
                </div>
              </div>
              {newProductError && <div className="hint-error">{newProductError}</div>}
              <div className="flex gap-2 mt-3">
                <button type="button" className="btn-accent btn-sm" disabled={savingProduct} onClick={saveProduct}>{savingProduct ? 'Saving…' : 'Save & Add to Order'}</button>
                <button type="button" className="btn-ghost btn-sm" onClick={() => { setShowNewProduct(false); setNewProduct(EMPTY_PRODUCT); setNewProductError('') }}>Cancel</button>
              </div>
              <div className="hint">Saved to the product catalogue with its unit — future orders and bills pre-set the unit and route automatically.</div>
            </div>
          )}
          <table>
            <thead><tr><th>Product</th><th className="num" style={{ width: 120 }}>Qty Ordered</th><th style={{ width: 110 }}>UOM</th><th className="num" style={{ width: 150 }}>Expected Rate</th><th className="num" style={{ width: 110 }}>Value</th><th className="w-8"></th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    <ProductPicker options={pickerOptions} text={l.product} onText={(t) => updateLine(l.id, { product: t })} onSelect={(o) => updateLine(l.id, { product: o.value })} placeholder="Type or pick a product" className={`!py-[6px] ${errors[`line-${l.id}-product`] ? '!border-danger' : ''}`} inputProps={{ style: { marginBottom: 0 } }} />
                    {errors[`line-${l.id}-product`] && <div className="hint-error">{errors[`line-${l.id}-product`]}</div>}
                  </td>
                  <td className="num">
                    <input type="number" min={0} step={unitFor(l) === 'kg' ? '0.001' : '1'} value={Number.isNaN(l.qty) ? '' : l.qty} onChange={(e) => updateLine(l.id, { qty: e.target.value === '' ? NaN : Number(e.target.value) })} className={`!w-20 !py-[6px] text-right ${errors[`line-${l.id}-qty`] ? '!border-danger' : ''}`} style={{ marginBottom: 0 }} />
                    {errors[`line-${l.id}-qty`] && <div className="hint-error">{errors[`line-${l.id}-qty`]}</div>}
                  </td>
                  <td style={{ width: 110 }}>
                    <select value={unitFor(l)} onChange={(e) => updateLine(l.id, { unit: e.target.value as Unit })} className="!w-24 !py-[6px] !px-[6px] !text-xs" style={{ marginBottom: 0 }} aria-label="Unit">
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  <td className="num">
                    <div className="inline-flex items-center gap-1">
                      <span className="text-ink-soft text-xs">₹</span>
                      <input type="number" min={0} step="0.01" value={Number.isNaN(l.rate) ? '' : l.rate} onChange={(e) => updateLine(l.id, { rate: e.target.value === '' ? NaN : Number(e.target.value) })} className={`!w-24 !py-[6px] text-right ${errors[`line-${l.id}-rate`] ? '!border-danger' : ''}`} style={{ marginBottom: 0 }} />
                    </div>
                    {errors[`line-${l.id}-rate`] && <div className="hint-error">{errors[`line-${l.id}-rate`]}</div>}
                  </td>
                  <td className="num font-semibold">{formatINR((l.qty || 0) * (l.rate || 0))}</td>
                  <td className="text-center"><button type="button" onClick={() => removeLine(l.id)} disabled={lines.length === 1} className="text-ink-soft hover:text-danger disabled:opacity-30 text-base leading-none" title="Remove line">×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {errors.lines && <div className="hint-error">{errors.lines}</div>}
          <button type="button" className="btn-ghost" style={{ marginTop: 10 }} onClick={addLine}>+ Add another item</button>
          <div className="hint">UOM follows the product catalogue (kg goes to repacking, pkt/Tin/ltr sold as purchased). Expected rates are indicative — the purchase bill records what was actually charged.</div>
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div>
              <label>Items</label>
              <input value={`${lines.filter((l) => l.product.trim()).length} item${lines.filter((l) => l.product.trim()).length === 1 ? '' : 's'}`} disabled />
            </div>
            <div>
              <label>Quantity</label>
              <input value={Object.entries(lines.filter((l) => l.product.trim()).reduce<Record<string, number>>((m, l) => { const u = unitFor(l); m[u] = (m[u] || 0) + (l.qty || 0); return m }, {})).map(([u, q]) => `${q.toLocaleString('en-IN')} ${u}`).join(' + ') || '—'} disabled />
            </div>
            <div>
              <label>Expected Delivery</label>
              <input value={expectedDate ? formatDate(expectedDate) : '—'} disabled />
            </div>
            <div>
              <label>Expected Value</label>
              <input value={formatINR(total)} disabled className="!font-bold !text-[15px]" />
            </div>
          </div>
          <div className="btnrow">
            <button type="button" className="btn-primary" onClick={() => submit('Sent')} disabled={busy !== null}>{busy === 'Sent' ? 'Sending…' : 'Send PO to Supplier'}</button>
            <button type="button" className="btn-ghost" onClick={() => submit('Draft')} disabled={busy !== null}>{busy === 'Draft' ? 'Saving…' : 'Save as Draft'}</button>
          </div>
        </div>

        <div className="card mt-[18px]">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>Open &amp; Recent Purchase Orders</h2>
            <button type="button" className={`btn-ghost btn-xs ${showAll ? 'on' : ''}`} onClick={() => setShowAll((v) => !v)}>{showAll ? 'Showing all' : 'Show all'}</button>
          </div>
          {summary && <div className="hint" style={{ marginTop: 4, marginBottom: 8 }}>{summary.open} open ({formatINR(summary.openValue)}) · {summary.drafts} draft{summary.drafts === 1 ? '' : 's'} · {summary.overdue} overdue</div>}
          <table>
            <thead><tr><th>PO No.</th><th>Supplier</th><th>Order Date</th><th>Expected</th><th className="num">Ordered</th><th className="num">Received So Far</th><th className="num">Value</th><th>Status</th></tr></thead>
            <tbody>
              {pos.length === 0 && <tr><td colSpan={8} className="text-ink-soft">No purchase orders yet.</td></tr>}
              {pos.map((po) => (
                <Fragment key={po.id}>
                  <tr onClick={() => setOpenId(openId === po.id ? null : po.id)} style={{ cursor: 'pointer' }} className={po.overdueDays > 0 ? 'bg-[#FDF6F4]' : ''}>
                    <td className="font-semibold"><Link to={`/dashboard/procurement/purchase-orders/${po.id}`} className="text-primary-500 hover:underline" onClick={(e) => e.stopPropagation()}>{po.poNumber}</Link>{po.overdueDays > 0 && <div className="text-[10.5px] text-danger font-normal">{po.overdueDays}d overdue</div>}</td>
                    <td>{po.supplierName}</td>
                    <td>{formatDate(po.orderDate)}</td>
                    <td>{po.expectedDate ? formatDate(po.expectedDate) : '—'}</td>
                    <td className="num">{po.orderedLabel}</td>
                    <td className="num">{po.receivedLabel}</td>
                    <td className="num">{formatINR(po.value)}</td>
                    <td><span className={`badge ${statusBadge[po.status]}`}>{po.status}</span></td>
                  </tr>
                  {openId === po.id && (
                    <tr>
                      <td colSpan={8} className="!py-2" style={{ background: '#FAFAF7' }}>
                        <div className="text-[11.5px] text-ink-soft mb-1">Ordered {formatDate(po.orderDate)}{po.expectedDate ? ` · expected ${formatDate(po.expectedDate)}` : ''} · value {formatINR(po.value)}{po.bills.length ? ` · bills: ${po.bills.map((b) => b.billNo).join(', ')}` : ''}</div>
                        {po.lines.map((l) => (
                          <div key={l.product} className="flex justify-between gap-3 py-1 text-[12px] border-b border-[#EEEDE7] last:border-b-0">
                            <div>{l.product} <span className="text-ink-soft">· {l.quantity.toLocaleString('en-IN')} {l.unit} @ {formatINR(l.rate)}</span></div>
                            <div className={l.remaining > 0 && po.status !== 'Draft' ? 'text-accent-500 font-semibold' : 'font-semibold'}>{po.status === 'Draft' ? '—' : `${l.received.toLocaleString('en-IN')} / ${l.quantity.toLocaleString('en-IN')} ${l.unit}`}</div>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2 flex-wrap">
                          <Link to={`/dashboard/procurement/purchase-orders/${po.id}`} className="btn-ghost btn-xs" onClick={(e) => e.stopPropagation()}>View / Print / PDF →</Link>
                          {po.status === 'Draft' && <button type="button" className="btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); editDraft(po) }}>Edit draft</button>}
                          {po.status === 'Draft' && <button type="button" className="btn-primary btn-xs" onClick={(e) => { e.stopPropagation(); action(po, 'send') }} disabled={busy !== null}>Send to supplier</button>}
                          {po.status === 'Draft' && <button type="button" className="btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); action(po, 'delete') }} disabled={busy !== null}>Delete draft</button>}
                          {(po.status === 'Sent' || po.status === 'Partially Received') && (
                            <button type="button" className="btn-accent btn-xs" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/procurement/purchase-bills?po=${po.id}`) }}>Record purchase bill →</button>
                          )}
                          {(po.status === 'Sent' || po.status === 'Partially Received') && <button type="button" className="btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); action(po, 'close') }} disabled={busy !== null}>Close (stop expecting)</button>}
                          {(po.status === 'Sent' || po.status === 'Partially Received') && <button type="button" className="btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); action(po, 'cancel') }} disabled={busy !== null}>Cancel PO</button>}
                          {po.bills.map((b) => <Link key={b.id} to={`/dashboard/procurement/purchase-bills/${b.id}`} className="btn-ghost btn-xs" onClick={(e) => e.stopPropagation()}>{b.billNo} →</Link>)}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {hint && <div className="hint">{hint}</div>}
        </div>
      </div>
    </div>
  )
}
